import { TicketInboxFilter } from "@jugl-web/rest-api/tickets/models/common-types/TicketInboxFilter";
import { Button } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import { TicketCreationBlockedTooltip } from "@web-src/modules/client-support/components/TicketCreationBlockedTooltip";
import { FC, SVGProps } from "react";
import { ReactComponent as UnassignedIcon } from "./assets/unassigned.svg";
import { ReactComponent as AssignedToMeIcon } from "./assets/assigned-to-me.svg";
import emptyStateAnimation from "./assets/empty-state-animation.json";
import { ReactComponent as InProgressIcon } from "./assets/in-progress.svg";
import { ReactComponent as IssuedIcon } from "./assets/issued.svg";
import { ReactComponent as OnCustomerIcon } from "./assets/on-customer.svg";
import { ReactComponent as OnHoldIcon } from "./assets/on-hold.svg";
import { ReactComponent as PlusIcon } from "./assets/plus.svg";
import { ReactComponent as ResolvedIcon } from "./assets/resolved.svg";

interface TicketsEmptyStateProps {
  variant: TicketInboxFilter;
  onCreateTicket: () => void;
  isCreateBlocked?: boolean;
}

export const TicketsEmptyState: FC<TicketsEmptyStateProps> = ({
  variant,
  onCreateTicket,
  isCreateBlocked,
}) => {
  const { t } = useTranslations();

  const { title, subtitle } = variantToCopy[variant] ?? FALLBACK_COPY;

  const IconComponent = variant === "all" ? null : variantToIcon[variant];

  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="flex w-[500px] flex-col items-center gap-8 text-center">
        {IconComponent ? (
          <div className="relative flex size-[150px] items-center justify-center drop-shadow-[-17.719px_19.687px_11.616px_rgba(87,68,111,0.16)]">
            <div className="absolute top-1/2 left-1/2 size-[126px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white" />
            <IconComponent aria-hidden className="relative shrink-0" />
          </div>
        ) : (
          <Lottie
            options={{ animationData: emptyStateAnimation }}
            width={150}
            height={150}
          />
        )}

        <div className="flex flex-col items-center gap-2">
          <h2 className="m-0 font-secondary text-2xl leading-normal font-medium text-black">
            {title}
          </h2>
          <p className="m-0 font-secondary text-base leading-normal text-[#828282]">
            {subtitle}
          </p>
        </div>

        {isCreateBlocked ? (
          <TicketCreationBlockedTooltip
            renderTrigger={({ props, ref }) => (
              <div ref={ref} {...props}>
                <Button
                  variant="primary"
                  size="sm"
                  isDisabled
                  iconEnd={<PlusIcon />}
                >
                  {t({
                    id: "client-support-page.inbox-sidebar.create-ticket",
                    defaultMessage: "Create Ticket",
                  })}
                </Button>
              </div>
            )}
          />
        ) : (
          <Button
            variant="primary"
            size="sm"
            iconEnd={<PlusIcon />}
            onClick={onCreateTicket}
          >
            {t({
              id: "client-support-page.inbox-sidebar.create-ticket",
              defaultMessage: "Create Ticket",
            })}
          </Button>
        )}
      </div>
    </div>
  );
};

const variantToIcon: Record<
  Exclude<TicketInboxFilter, "all">,
  FC<SVGProps<SVGSVGElement>>
> = {
  assigned_to_user: AssignedToMeIcon,
  unassigned: UnassignedIcon,
  in_progress: InProgressIcon,
  on_customer: OnCustomerIcon,
  on_hold: OnHoldIcon,
  issued: IssuedIcon,
  resolved: ResolvedIcon,
};

const variantToCopy: Record<
  TicketInboxFilter,
  { title: string; subtitle: string }
> = {
  all: {
    title: "No open tickets",
    subtitle: "Your team have no active tickets at the moment",
  },
  unassigned: {
    title: "No open tickets",
    subtitle: "Your team have no unassigned tickets at the moment",
  },
  assigned_to_user: {
    title: "No open tickets",
    subtitle: "There are no tickets assigned to you at the moment",
  },
  in_progress: {
    title: "No open tickets",
    subtitle: `There are no "In Progress" tickets at the moment`,
  },
  on_customer: {
    title: "No open tickets",
    subtitle: `There are no "On Customer" tickets at the moment`,
  },
  on_hold: {
    title: "No open tickets",
    subtitle: `There are no "On Hold" tickets at the moment`,
  },
  issued: {
    title: "No open tickets",
    subtitle: `There are no "Issued" tickets at the moment`,
  },
  resolved: {
    title: "No resolved tickets",
    subtitle: `There are no "Resolved" tickets at the moment`,
  },
};

const FALLBACK_COPY = {
  title: "No tickets",
  subtitle: "There are no tickets at the moment",
};
